import getFirebase from './firebase';
import { configService } from './configService';
import { toast } from 'react-toastify';
import { generateApplicationId } from '../utils/applicationIdGenerator';
import { sendEmail } from './notificationService';
import { APPLICATION_STATUS, RENEWAL_STATUS } from '../Types/constants';
import { memberRenewalEmail } from '../components/utilities/emailTemplates';
import { serviceRequestsTypes } from 'helpers/utils';

const firebase = getFirebase();
const db = firebase.firestore();

/**
 * Testing mode configuration
 * Set USE_DRY_RUN_COLLECTION to true to test against migrated data in membersDryRun collection
 * Set to false for production use with regular members collection
 */
const USE_DRY_RUN_COLLECTION = false; // Toggle this for testing
const MEMBERS_COLLECTION = USE_DRY_RUN_COLLECTION ? 'membersDryRun' : 'members';

/**
 * Member Service - Handles new data structure operations
 *
 * New Data Structure:
 * - Member level: Static data (PersonalDetails, ContactDetails, QualificationDetails, etc.)
 * - Yearly applications: Dynamic data (CPDs, invoices, application-specific data)
 *
 * Testing Mode:
 * - USE_DRY_RUN_COLLECTION = true: Uses 'membersDryRun' collection for testing
 * - USE_DRY_RUN_COLLECTION = false: Uses 'members' collection for production
 */

/**
 * Save member's static data (PersonalDetails, ContactDetails, etc.)
 * @param {string} uid - User ID
 * @param {Object} memberData - Member data object with static fields
 * @returns {Promise} - Success/failure response
 */
export const saveMemberStaticData = async (uid, memberData) => {
  try {
    // Only save static fields to member level
    const staticFields = {};

    if (memberData.PersonalDetails) {
      staticFields.PersonalDetails = memberData.PersonalDetails;
    }

    if (memberData.ContactDetails) {
      staticFields.ContactDetails = memberData.ContactDetails;
    }

    if (memberData.Qualifications) {
      staticFields.Qualifications = memberData.Qualifications;
    }

    if (memberData.QualificationDetails) {
      staticFields.QualificationDetails = memberData.QualificationDetails;
    }

    if (memberData.WorkExperience) {
      staticFields.WorkExperience = memberData.WorkExperience;
    }

    if (memberData.WorkExperienceDetails) {
      staticFields.WorkExperienceDetails = memberData.WorkExperienceDetails;
    }

    if (memberData.ProfessionalRegistrations) {
      staticFields.ProfessionalRegistrations = memberData.ProfessionalRegistrations;
    }

    if (memberData.ProfessionalRegistrationDetails) {
      staticFields.ProfessionalRegistrationDetails = memberData.ProfessionalRegistrationDetails;
    }

    if (memberData.StatutoryDeclaration) {
      staticFields.StatutoryDeclaration = memberData.StatutoryDeclaration;
    }

    await db
      .collection(MEMBERS_COLLECTION)
      .doc(uid)
      .set(staticFields, { merge: true });

    return { success: true };
  } catch (error) {
    console.error('❌ Error saving member static data:', error);
    toast.error('An error occurred while saving member data!');
    return { success: false, error };
  }
};

/**
 * Create or update a yearly application with CPDs and invoices
 * @param {string} uid - User ID
 * @param {number} year - Application year
 * @param {Object} applicationData - Application data (CPDs, invoices, etc.)
 * @returns {Promise} - Success/failure response
 */
export const saveYearlyApplication = async (uid, year, applicationData) => {
  try {
    const applicationId = generateApplicationId(uid, year);

    // Structure for yearly application
    const yearlyData = {
      applicationId,
      year,
      uid,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      invoices: applicationData.invoices || [],
      //status: applicationData.status || 'PENDING',
      ...applicationData
    };

    // If CPDs are provided, include them
    if (applicationData.CPDs) {
      yearlyData.CPDs = applicationData.CPDs;
    }

    // Applications is a FIELD, not a subcollection - save to nested field
    const memberRef = db.collection(MEMBERS_COLLECTION).doc(uid);

    await memberRef.set(
      {
        applications: {
          [year.toString()]: yearlyData
        }
      },
      { merge: true }
    );
    return { success: true, applicationId };
  } catch (error) {
    console.error('❌ Error saving yearly application:', { error, uid, year, applicationData });
    toast.error('An error occurred while saving application data!');
    return { success: false, error };
  }
};

/**
 * Get member's static data only
 * @param {string} uid - User ID
 * @returns {Promise} - Member static data
 */
export const getMemberStaticData = async uid => {
  try {
    const doc = await db
      .collection(MEMBERS_COLLECTION)
      .doc(uid)
      .get();

    if (doc.exists) {
      const data = doc.data();
      // Filter out applications collection data if it exists
      const { applications, ...staticData } = data;
      return { success: true, data: staticData };
    }

    console.warn('[memberService] getMemberStaticData: document missing', {
      collection: MEMBERS_COLLECTION,
      uid
    });

    return { success: false, error: 'Member not found' };
  } catch (error) {
    console.error('❌ Error fetching member static data:', { error, uid });
    return { success: false, error };
  }
};

/**
 * Get a specific yearly application
 * @param {string} uid - User ID
 * @param {number} year - Application year
 * @returns {Promise} - Yearly application data
 */
export const getYearlyApplication = async (uid, year) => {
  try {
    const path = `${MEMBERS_COLLECTION}/${uid}`;
    // Applications is a FIELD (object), not a subcollection!
    const doc = await db
      .collection(MEMBERS_COLLECTION)
      .doc(uid)
      .get();

    if (doc.exists) {
      const memberData = doc.data();
      const applications = memberData.applications || {};
      const yearData = applications[year.toString()];

      if (yearData) {
        return { success: true, data: yearData };
      }
    }
    return { success: false, error: 'Application not found' };
  } catch (error) {
    console.error('❌ Error fetching yearly application:', { error, uid, year });
    return { success: false, error };
  }
};

/**
 * Get all yearly applications for a member
 * @param {string} uid - User ID
 * @returns {Promise} - Array of yearly applications sorted by year descending
 */
export const getAllYearlyApplications = async uid => {
  try {
    // Applications is a FIELD (object), not a subcollection!
    const doc = await db
      .collection(MEMBERS_COLLECTION)
      .doc(uid)
      .get();

    if (!doc.exists) {
      return { success: true, data: [] };
    }

    const memberData = doc.data();
    const applicationsObj = memberData.applications || {};

    // Convert applications object to array and sort by year descending
    const applications = Object.keys(applicationsObj)
      .map(year => ({
        id: year,
        year: parseInt(year),
        ...applicationsObj[year]
      }))
      .sort((a, b) => b.year - a.year);
    return { success: true, data: applications };
  } catch (error) {
    console.error('❌ Error fetching yearly applications:', { error, uid });
    return { success: false, error };
  }
};

/**
 * Get complete member data (static + specific year application)
 * @param {string} uid - User ID
 * @param {number} year - Optional year for application data
 * @returns {Promise} - Complete member data
 */
export const getCompleteMemberData = async (uid, year = null) => {
  try {
    // Get static data
    const staticResult = await getMemberStaticData(uid);
    if (!staticResult.success) {
      return staticResult;
    }

    let completeData = staticResult.data;
    // If year is specified, try to get that application
    if (year) {
      const appResult = await getYearlyApplication(uid, year);
      if (appResult.success) {
        completeData.currentApplication = appResult.data;
      } else {
        // If specified year not found, fall back to most recent application
        const allAppsResult = await getAllYearlyApplications(uid);
        if (allAppsResult.success && allAppsResult.data.length > 0) {
          completeData.currentApplication = allAppsResult.data[0];
        }
      }
    } else {
      // Get most recent application
      const allAppsResult = await getAllYearlyApplications(uid);
      if (allAppsResult.success && allAppsResult.data.length > 0) {
        completeData.currentApplication = allAppsResult.data[0];
      }
    }

    // FALLBACK: If no CPDs found in application, check legacy location in static data
    // This handles cases where data hasn't been migrated to applications subcollection yet
    if (!completeData.currentApplication?.CPDs && completeData.CPDs) {
      if (!completeData.currentApplication) {
        completeData.currentApplication = {};
      }
      completeData.currentApplication.CPDs = completeData.CPDs;
    }

    if (!completeData.currentApplication) {
    }

    return { success: true, data: completeData };
  } catch (error) {
    console.error('❌ Error fetching complete member data:', { error, uid, year });
    return { success: false, error };
  }
};

/**
 * Add an invoice to a yearly application
 * @param {string} uid - User ID
 * @param {number} year - Application year
 * @param {Object} invoiceData - Invoice data to add
 * @returns {Promise} - Success/failure response
 */
export const addInvoiceToApplication = async (uid, year, invoiceData) => {
  try {
    // Applications is a FIELD, not a subcollection - update nested field
    const memberRef = db.collection(MEMBERS_COLLECTION).doc(uid);

    await memberRef.update({
      [`applications.${year}.invoices`]: firebase.firestore.FieldValue.arrayUnion(invoiceData),
      [`applications.${year}.updatedAt`]: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('❌ Error adding invoice to application:', { error, uid, year, invoiceData });
    return { success: false, error };
  }
};

/**
 * Add CPDs to a yearly application
 * @param {string} uid - User ID
 * @param {number} year - Application year
 * @param {Array} cpds - Array of CPD data
 * @returns {Promise} - Success/failure response
 */
export const addCPDsToApplication = async (uid, year, cpds) => {
  try {
    // Applications is a FIELD, not a subcollection - update nested field
    const memberRef = db.collection(MEMBERS_COLLECTION).doc(uid);

    // First check if applications.year exists, if not create it
    const doc = await memberRef.get();
    const memberData = doc.data();
    const applications = memberData?.applications || {};

    if (!applications[year]) {
      applications[year] = {
        year: year,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }

    // Now update the CPDs
    applications[year].CPDs = cpds;
    applications[year].updatedAt = new Date().toISOString();

    await memberRef.update({ applications });
    return { success: true };
  } catch (error) {
    console.error('❌ Error adding CPDs to application:', { error, uid, year, cpds });
    return { success: false, error };
  }
};

/**
 * Create a new membership/renewal application for current year
 * @param {string} uid - User ID
 * @param {Object} applicationData - Application data
 * @returns {Promise} - Success/failure response with application ID
 */
export const createCurrentYearApplication = async (uid, applicationData) => {
  const currentYear = await configService.getCurrentActiveYear();

  const result = await saveYearlyApplication(uid, currentYear, {
    ...applicationData,
    type: applicationData.type || 'membership',
    //status: 'PENDING_VERIFICATION',
    createdAt: new Date().toISOString()
  });

  return result;
};

export const getMemberRenwalStatus = async uid => {
  const currentYear = await configService.getCurrentActiveYear();
  try {
    const doc = await db
      .collection(MEMBERS_COLLECTION)
      .doc(uid)
      .get();

    const data = doc.data();
    const applications = data?.applications || {};
    const yearData = applications[currentYear.toString()];

    return yearData?.renewalStatus || RENEWAL_STATUS.NOT_STARTED;
  } catch (error) {
    console.log('❌ Error fetching member renewal status:', { error, uid, currentYear });
    return RENEWAL_STATUS.NOT_STARTED;
  }
};

/**
 * Fetch members whose current year's application has not started renewal
 * @returns {Promise<{success: boolean, data: Array}>}
 */
export const getNewMembersApplications = async (role, showRenewals = false) => {
  const currentYear = await configService.getCurrentActiveYear();

  try {
    const [membersSnapshot, authoritySnapshot, invoicesSnapshot] = await Promise.all([
      db.collection(MEMBERS_COLLECTION).get(),
      db.collection('authority').get(),
      db.collection('invoices').get()
    ]);

    if (membersSnapshot.empty) {
      console.warn('[memberService] getMembersWithPendingRenewals: no members found');
      return { success: true, data: [] };
    }

    const authorityMap = authoritySnapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data() || {};
      return acc;
    }, {});

    const invoiceMap = invoicesSnapshot.docs.reduce((acc, doc) => {
      const invoiceData = doc.data() || {};
      acc[doc.id] = invoiceData;
      if (invoiceData.invoiceId) {
        acc[invoiceData.invoiceId] = invoiceData;
      }
      return acc;
    }, {});

    const pendingMembers = membersSnapshot.docs
      .map(doc => {
        const data = doc.data() || {};
        const applications = data.applications || {};
        const currentApplication = applications[currentYear?.toString()] || {};
        const renewalStatus = currentApplication.renewalStatus;
        const authority = authorityMap[doc.id] || {};
        const invoiceIds = Array.isArray(currentApplication.invoices) ? currentApplication.invoices : [];
        const invoices = invoiceIds.map(id => invoiceMap[id]).filter(Boolean);
        const feePayable = invoices.reduce((sum, inv) => sum + (Number(inv.amount) || 0), 0);
        const currency = invoices.find(inv => inv.currency)?.currency || null;
        const paymentStatus = invoices.find(inv => inv.paymentStatus)?.paymentStatus || null;
        const first = authority.name ? String(authority.name).trim() : '';
        const last = authority.surname ? String(authority.surname).trim() : '';
        const name = `${first} ${last}`.trim();
        const level = authority.level;
        const type = authority?.applicationFormType || 'N/A';
        const applicationFormType = authority?.applicationFormType || 'existing';

        const normalizedStatus = renewalStatus ?? RENEWAL_STATUS.NOT_STARTED;

        const passesApplicationLevelFilter = () => {
          if (showRenewals) return true;

          if (role?.length === 1 || role.length === 2) {
            if (role.includes('VERIFICATION')) return level === APPLICATION_STATUS.PENDING_VERIFICATION_MEMBER;
            if (role.includes('APPROVAL')) return level === APPLICATION_STATUS.PENDING_APPROVAL_MEMBER;
          }

          return [
            APPLICATION_STATUS.PENDING_VERIFICATION_MEMBER,
            APPLICATION_STATUS.PENDING_APPROVAL_MEMBER,
            APPLICATION_STATUS.APPROVED_MEMBER
          ].includes(level);
        };

        const passesRenewalFilter = () => {
          if (role?.length === 1 || role.length === 2) {
            if (role.includes('VERIFICATION')) return renewalStatus === RENEWAL_STATUS.RENEWAL_PENDING_VERIFICATION;
            if (role.includes('APPROVAL')) return renewalStatus === RENEWAL_STATUS.RENEWAL_PENDING_APPROVAL;
          }

          return [
            RENEWAL_STATUS.RENEWAL_PENDING_APPROVAL,
            RENEWAL_STATUS.RENEWAL_PENDING_VERIFICATION,
            RENEWAL_STATUS.RENEWED
          ].includes(normalizedStatus);
        };

        const isPending = showRenewals
          ? passesRenewalFilter()
          : normalizedStatus === RENEWAL_STATUS.NOT_STARTED && passesApplicationLevelFilter();

        return {
          id: doc.id,
          renewalStatus: renewalStatus ?? RENEWAL_STATUS.NOT_STARTED,
          currentApplication,
          isPending,
          name,
          level,
          feePayable,
          paymentStatus,
          type,
          applicationFormType,
          currency
        };
      })
      .filter(entry => entry.isPending)
      .map(
        ({
          id,
          currentApplication,
          renewalStatus,
          name,
          level,
          feePayable,
          paymentStatus,
          type,
          applicationFormType,
          currency
        }) => ({
          id,
          currentApplication,
          renewalStatus,
          name,
          level,
          feePayable,
          paymentStatus,
          type,
          applicationFormType,
          currency
        })
      );

    return { success: true, data: pendingMembers };
  } catch (error) {
    console.error('[memberService] getMembersWithPendingRenewals: failed', error);
    return { success: false, error };
  }
};

/**
 * Open renewal period: create renewal applications for all members and email them.
 * @param {string} adminToken - Bearer token for email service
 * @param {object} options
 * @param {number} [options.year] - Target year (defaults to current active year)
 * @param {Array<string>} [options.memberIds] - Optional whitelist of member IDs to process
 * @param {boolean} [options.skipEmails=false] - Skip sending emails (useful for dry runs/tests)
 */
export const openRenewalPeriod = async (adminToken, { year, memberIds, skipEmails = false } = {}) => {
  const targetYear = year || (await configService.getCurrentActiveYear());

  try {
    let memberDocs;

    if (Array.isArray(memberIds) && memberIds.length > 0) {
      const fetched = await Promise.all(
        memberIds.map(async uid => {
          const doc = await db
            .collection(MEMBERS_COLLECTION)
            .doc(uid)
            .get();
          return { uid, doc };
        })
      );

      memberDocs = fetched;
    } else {
      const membersSnap = await db.collection(MEMBERS_COLLECTION).get();
      if (membersSnap.empty) {
        return { success: true, processed: 0, errors: [] };
      }
      memberDocs = membersSnap.docs.map(doc => ({ doc, uid: doc.id }));
    }

    const results = { success: true, processed: 0, errors: [] };

    for (const entry of memberDocs) {
      const uid = entry.uid || entry.doc?.id;
      const doc = entry.doc;

      if (!doc?.exists) {
        results.errors.push({ uid, reason: 'member not found' });
        continue;
      }

      const member = doc.data() || {};

      // Fetch authority level
      let level = null;
      try {
        const authSnap = await db
          .collection('authority')
          .doc(uid)
          .get();
        level = authSnap.exists ? authSnap.data()?.level : null;
      } catch (err) {
        console.error('❌ openRenewalPeriod: authority lookup failed', { uid, err });
      }

      if (level === 'ADMIN') {
        results.skipped = (results.skipped || 0) + 1;
        continue;
      }

      const isApproved = level === APPLICATION_STATUS.APPROVED_MEMBER;
      const renewalStatus = isApproved ? RENEWAL_STATUS.PROCESSING : RENEWAL_STATUS.NOT_STARTED;
      const type = isApproved ? serviceRequestsTypes.renewal : serviceRequestsTypes.membership;

      try {
        await saveYearlyApplication(uid, targetYear, {
          type,
          renewalStatus
        });
        results.processed += 1;
      } catch (err) {
        console.error('❌ openRenewalPeriod: failed to create application', { uid, err });
        results.errors.push({ uid, reason: 'saveYearlyApplication failed', error: err?.message || String(err) });
        continue;
      }

      // Send renewal email
      const email = member?.ContactDetails?.email || member?.ContactDetails?.Email || member?.email;
      if (!email) {
        continue;
      }

      if (!skipEmails) {
        try {
          const name =
            member?.PersonalDetails?.FullName ||
            member?.PersonalDetails?.fullName ||
            [
              member?.PersonalDetails?.FirstName || member?.PersonalDetails?.firstName,
              member?.PersonalDetails?.LastName || member?.PersonalDetails?.lastName
            ]
              .filter(Boolean)
              .join(' ') ||
            'Member';

          const template = memberRenewalEmail(name, uid, isApproved ? 'process' : 'review');

          const toOverride = process.env.NODE_ENV === 'development' ? [process.env.REACT_APP_EMAIL_RECIPIENT] : [email];

          await sendEmail(
            {
              to: toOverride,
              ...template,
              isHtml: true
            },
            adminToken
          );
        } catch (err) {
          console.error('openRenewalPeriod: failed to send email', { uid, email, err });
          results.errors.push({ uid, reason: 'email failed', error: err?.message || String(err) });
        }
      }
    }

    return results;
  } catch (error) {
    console.error('❌ openRenewalPeriod: fatal error', error);
    return { success: false, error };
  }
};

/**
 * Update application status
 * @param {string} uid - User ID
 * @param {number} year - Application year
 * @param {string} status - New status
 * @returns {Promise} - Success/failure response
 */
export const updateApplicationStatus = async (uid, year, status) => {
  try {
    const memberRef = db.collection(MEMBERS_COLLECTION).doc(uid);
    await memberRef.update({
      [`applications.${year}.status`]: status,
      [`applications.${year}.updatedAt`]: new Date().toISOString()
    });

    return { success: true };
  } catch (error) {
    console.error('❌ Error updating application status:', error);
    return { success: false, error };
  }
};

export const updateRenewalStatus = async (uid, renewalStatus, expiryDate, year = null) => {
  try {
    if (!year) {
      year = await configService.getCurrentActiveYear();
    }
    const memberRef = db.collection(MEMBERS_COLLECTION).doc(uid);
    // Applications are stored as a map field on the member document (applications.{year})
    await memberRef.update({
      [`applications.${year}.renewalStatus`]: renewalStatus,
      [`applications.${year}.approvalDate`]: renewalStatus === RENEWAL_STATUS.RENEWED ? new Date().toISOString() : null,
      [`applications.${year}.expiryDate`]: renewalStatus === RENEWAL_STATUS.RENEWED ? expiryDate : null,
      [`applications.${year}.updatedAt`]: new Date().toISOString()
    });

    return { success: true };
  } catch (error) {
    console.error('❌ Error updating renewal status:', error);
    return { success: false, error };
  }
};

/**
 * Check if service is using dry run collection for testing
 * @returns {boolean} - True if using membersDryRun, false if using production members
 */
export const isUsingDryRunCollection = () => USE_DRY_RUN_COLLECTION;

/**
 * Get the current collection name being used
 * @returns {string} - Collection name (members or membersDryRun)
 */
export const getCurrentCollection = () => MEMBERS_COLLECTION;

export default {
  saveMemberStaticData,
  saveYearlyApplication,
  getMemberStaticData,
  getYearlyApplication,
  getAllYearlyApplications,
  getCompleteMemberData,
  addInvoiceToApplication,
  addCPDsToApplication,
  createCurrentYearApplication,
  updateApplicationStatus,
  updateRenewalStatus,
  getMemberRenwalStatus,
  isUsingDryRunCollection,
  getCurrentCollection,
  getNewMembersApplications
};
